import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Search, Trash2, History, Loader2, ShieldAlert, ArrowLeft, CheckCircle2, Ban, Package } from 'lucide-react';
import { toast } from 'sonner';
import { searchDispatchNotesForPurge, getDispatchNotePurgeHistory } from '../../lib/api';
import DispatchNotePurgeDialog from '../../components/DispatchNotePurgeDialog';

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function DispatchNotePurge() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [includeLinked, setIncludeLinked] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('search');

  const runSearch = useCallback(async (term, linked) => {
    setSearching(true);
    try {
      const { data } = await searchDispatchNotesForPurge(term, linked);
      setResults(data.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    runSearch('', false);
  }, [runSearch]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await getDispatchNotePurgeHistory();
      setHistory(data.records || []);
    } catch {
      toast.error('Failed to load purge history');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  const handlePurged = (id) => {
    setResults((prev) => prev.filter((r) => r.id !== id));
  };

  const statusColor = (s) => {
    if (s === 'dispatched') return 'bg-green-100 text-green-800';
    if (s === 'locked') return 'bg-blue-100 text-blue-800';
    if (s === 'ready') return 'bg-sky-100 text-sky-800';
    return 'bg-zinc-100 text-zinc-700';
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="dispatch-note-purge-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')} data-testid="btn-back-to-dm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-orange-600" /> Dispatch Note Purge
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Soft-delete standalone dispatch notes raised in error. Notes with a PO, invoice, batch details,
            returns or an open recall are blocked.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="search" data-testid="tab-dn-purge-search">
                <Search className="w-4 h-4 mr-1.5" /> Search &amp; Purge
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-dn-purge-history">
                <History className="w-4 h-4 mr-1.5" /> Purge History
              </TabsTrigger>
            </TabsList>

            <TabsContent value="search">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Input
                  placeholder="Search by dispatch number or customer... (blank = most recent standalone notes)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch(searchQuery, includeLinked)}
                  className="flex-1 min-w-[260px]"
                  data-testid="input-dn-purge-search"
                />
                <Button
                  onClick={() => runSearch(searchQuery, includeLinked)}
                  disabled={searching}
                  data-testid="btn-dn-purge-search"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-1.5">Search</span>
                </Button>
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={includeLinked}
                    onCheckedChange={(v) => {
                      const next = !!v;
                      setIncludeLinked(next);
                      runSearch(searchQuery, next);
                    }}
                    data-testid="checkbox-include-linked"
                  />
                  Include PO-linked orders (always blocked)
                </label>
              </div>

              {results.length > 0 ? (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead className="font-semibold">Note #</TableHead>
                        <TableHead className="font-semibold">Customer</TableHead>
                        <TableHead className="font-semibold">PO</TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                        <TableHead className="font-semibold text-center">Lines</TableHead>
                        <TableHead className="font-semibold text-center">Batch lines</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold">Eligibility</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.map((note) => (
                        <TableRow key={note.id} data-testid={`dn-purge-result-${note.dispatch_number}`}>
                          <TableCell className="font-mono font-medium">{note.dispatch_number}</TableCell>
                          <TableCell>{note.customer_name || '—'}</TableCell>
                          <TableCell className="font-mono text-sm">{note.po_number || '—'}</TableCell>
                          <TableCell>
                            <Badge className={statusColor(note.status)} variant="secondary">{note.status}</Badge>
                          </TableCell>
                          <TableCell className="text-center">{note.lines_count}</TableCell>
                          <TableCell className="text-center">
                            {note.batch_lines_count > 0 ? (
                              <Badge variant="outline" className="text-amber-700 border-amber-300">
                                <Package className="w-3 h-3 mr-1" />{note.batch_lines_count}
                              </Badge>
                            ) : '0'}
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(note.dispatch_date || note.created_at)}</TableCell>
                          <TableCell className="max-w-[280px]">
                            {note.eligible ? (
                              <span className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
                                <CheckCircle2 className="w-4 h-4" /> Eligible
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-start gap-1.5 text-amber-700 text-xs"
                                title={note.blockers.map((b) => b.message).join(' | ')}
                                data-testid={`dn-purge-blocked-${note.dispatch_number}`}
                              >
                                <Ban className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                {note.blockers.map((b) => b.message.split('—')[0].trim()).join('; ')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={!note.eligible}
                              onClick={() => setPurgeTarget(note)}
                              data-testid={`btn-dn-purge-${note.dispatch_number}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Purge
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                !searching && (
                  <div className="text-center py-8 text-muted-foreground" data-testid="dn-purge-empty">
                    No dispatch notes found
                  </div>
                )
              )}
            </TabsContent>

            <TabsContent value="history">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history...
                </div>
              ) : history.length > 0 ? (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40">
                        <TableHead className="font-semibold">Note #</TableHead>
                        <TableHead className="font-semibold">Customer</TableHead>
                        <TableHead className="font-semibold">Status at purge</TableHead>
                        <TableHead className="font-semibold text-center">Lines</TableHead>
                        <TableHead className="font-semibold">Reason</TableHead>
                        <TableHead className="font-semibold">Purged By</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((rec) => (
                        <TableRow key={rec.id} className="bg-orange-50/30" data-testid={`dn-purge-history-${rec.dispatch_number}`}>
                          <TableCell className="font-mono font-medium">{rec.dispatch_number}</TableCell>
                          <TableCell>{rec.customer_name || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="bg-zinc-100">{rec.status_at_deletion}</Badge>
                          </TableCell>
                          <TableCell className="text-center">{rec.lines_count}</TableCell>
                          <TableCell className="max-w-[260px]">
                            <p className="text-sm truncate" title={rec.reason}>{rec.reason}</p>
                          </TableCell>
                          <TableCell className="text-sm">{rec.deleted_by}</TableCell>
                          <TableCell className="text-sm">{formatDate(rec.deleted_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground" data-testid="dn-purge-history-empty">
                  No dispatch notes have been purged
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <DispatchNotePurgeDialog
        open={!!purgeTarget}
        dispatchId={purgeTarget?.id}
        dispatchNumber={purgeTarget?.dispatch_number}
        onClose={() => setPurgeTarget(null)}
        onPurged={handlePurged}
      />
    </div>
  );
}
